'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { stripLocalePrefix } from '@/i18n/surfaces'
import {
  startMarketingPageView,
  trackComparePageViewed,
  trackFooterLinkClicked,
  trackMarketingCtaClicked,
  trackMarketingScrollDepth,
  trackMarketingSectionViewed,
  trackPricingCompareOpened,
} from '@/lib/analytics'

const DEPTHS = [25, 50, 75, 100]
// A section counts as seen once 40% of it, or 40% of the viewport for sections taller than
// 2.5 screens, stays on screen for DWELL_MS. Low thresholds let IntersectionObserver report on
// tall sections. The dwell (and the settled-scroll depth read) keep the smooth scroll to the top
// after a client-side navigation from counting every section it sweeps past.
const THRESHOLDS = [0, 0.05, 0.1, 0.2, 0.3, 0.4]
const DWELL_MS = 600
const SCROLL_SETTLE_MS = 300

const sectionName = (el: HTMLElement) => el.dataset.section || el.id

/** Where on the page a CTA sits: its section, else the nav or footer. */
function locationOf(el: HTMLElement): string {
  const section = el.closest<HTMLElement>('[data-section], section[id]')
  if (section) return sectionName(section)
  if (el.closest('nav')) return 'nav'
  if (el.closest('footer')) return 'footer'
  return 'page'
}

/**
 * Marketing analytics without per-component wiring: sections (`section[id]` or `[data-section]`)
 * report when seen, `[data-cta]` elements when clicked, footer links, scroll depth, and
 * compare pages. Listeners are passive, timers and observers are cleared, and nothing re-renders on scroll.
 * Renders nothing.
 */
export function MarketingTracker() {
  const pathname = usePathname()

  // Per page view: sections, scroll depth, compare page.
  useEffect(() => {
    startMarketingPageView()

    const dwell = new Map<Element, ReturnType<typeof setTimeout>>()
    const io = new IntersectionObserver(
      entries => {
        const minVisible = 0.4 * window.innerHeight
        for (const e of entries) {
          const visible = e.intersectionRect.height
          const seen = visible > 0 && visible >= Math.min(0.4 * e.boundingClientRect.height, minVisible)
          if (!seen) {
            clearTimeout(dwell.get(e.target))
            dwell.delete(e.target)
          } else if (!dwell.has(e.target)) {
            const el = e.target as HTMLElement
            dwell.set(
              el,
              setTimeout(() => {
                trackMarketingSectionViewed({ section: sectionName(el) })
                io.unobserve(el)
              }, DWELL_MS),
            )
          }
        }
      },
      { threshold: THRESHOLDS },
    )
    document.querySelectorAll<HTMLElement>('main section[id], main [data-section]').forEach(el => io.observe(el))

    let settle: ReturnType<typeof setTimeout> | undefined
    const reportDepth = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight
      if (scrollable <= 0) return
      const percent = (window.scrollY / scrollable) * 100
      // 99, not 100: subpixel rounding can keep scrollY a hair short of the bottom.
      for (const depth of DEPTHS) if (percent >= Math.min(depth, 99)) trackMarketingScrollDepth({ depthPercent: depth })
    }
    const onScroll = () => {
      clearTimeout(settle)
      settle = setTimeout(reportDepth, SCROLL_SETTLE_MS)
    }
    window.addEventListener('scroll', onScroll, { passive: true })

    // Effects run child-first, so on a direct load PostHog is not initialized yet; a macrotask
    // runs after the provider's effect.
    const competitor = stripLocalePrefix(window.location.pathname).pathname.match(/^\/compare\/([^/]+)$/)?.[1]
    const initial = setTimeout(() => {
      if (competitor) trackComparePageViewed({ competitor })
    }, 0)

    return () => {
      io.disconnect()
      dwell.forEach(timer => clearTimeout(timer))
      clearTimeout(settle)
      clearTimeout(initial)
      window.removeEventListener('scroll', onScroll)
    }
  }, [pathname])

  // Clicks and <details> toggles, delegated. Capture phase, so a handler that stops
  // propagation (the showcase audio button, the mobile menu) cannot hide a click.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target instanceof Element ? e.target : null
      const cta = target?.closest<HTMLElement>('[data-cta]')
      if (cta) {
        trackMarketingCtaClicked({
          cta: cta.dataset.cta!,
          location: cta.dataset.ctaLocation ?? locationOf(cta),
          outlet: cta.dataset.outlet,
          target: cta.dataset.ctaTarget,
        })
      }
      const footerLink = target?.closest<HTMLAnchorElement>('footer a')
      if (footerLink) {
        trackFooterLinkClicked({
          label: footerLink.textContent?.trim() ?? '',
          column: footerLink.closest<HTMLElement>('[data-footer-column]')?.dataset.footerColumn ?? 'other',
          href: footerLink.getAttribute('href') ?? '',
        })
      }
    }
    const onToggle = (e: Event) => {
      const el = e.target
      if (el instanceof HTMLDetailsElement && el.open && el.dataset.trackOpen === 'pricing_compare') {
        trackPricingCompareOpened()
      }
    }
    document.addEventListener('click', onClick, true)
    document.addEventListener('toggle', onToggle, true)
    return () => {
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('toggle', onToggle, true)
    }
  }, [])

  return null
}
